import {
    AccountCircleIcon,
    AccountTreeIcon,
    AppBar,
    AssessmentIcon,
    Box,
    BusinessCenterIcon,
    Button,
    DashboardIcon,
    DescriptionIcon,
    ExtensionIcon,
    LanguageIcon,
    ListIcon,
    LocalOfferIcon,
    Menu,
    MenuItem,
    SearchIcon,
    SettingsIcon,
    TranslateIcon,
    ViewInArIcon,
    WorkIcon,
} from '@ds/coolshop';
import { useLanguageCtx } from '@modules/language';
import { UI_APPBAR_ROLES } from '@src/_new/modules/auth/appBarRoles';
import { CartNavButton } from '@src/_new/modules/cpq/components/Cart/CartNavButton';
import { useCustomPages } from '@src/_new/modules/custom-pages/hooks/useCustomPages';
import { resolveIcon } from '@src/_new/modules/custom-pages/utils/resolveIcon';
import { GlobalSearchInput } from '@src/_new/modules/global-search/components/GlobalSearchInput';
import { NotificationBell } from '@src/_new/modules/notifications';
import { useCompanyOverrides } from '@src/_new/overrides/hooks/useCompanyOverrides';
import {
    path as translationsPath,
    useTranslations,
} from '@src/components/translations/Translations';
import { useAuth } from '@src/hooks';
import { toTitleCase } from '@src/utils';
import { ReactNode, useContext, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { SettingsContext } from '../../modules/setting/SettingsContext';

type LinkToProps = {
    dest: string;
    disabled?: boolean;
    icon: ReactNode;
    label?: string;
};

const LinkTo = ({ dest, icon, disabled, label }: LinkToProps) => {
    const intl = useIntl();
    const { pathname } = useLocation();

    const navigate = useNavigate();

    const destination = `/${dest}`;

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        // Allow middle-click, right-click, and Ctrl/Cmd+click to work naturally
        // These will open in new tab/window, so we don't need to block them
        if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || disabled) {
            return;
        }

        e.preventDefault();
        navigate(destination);
    };

    const active = pathname.startsWith(destination);

    return (
        <Link
            to={destination}
            onClick={handleClick}
            style={{
                textDecoration: 'none',
                pointerEvents: disabled ? 'none' : 'auto',
                opacity: disabled ? 0.5 : 1,
            }}
        >
            <Button
                disabled={disabled}
                startIcon={icon}
                variant="text"
                color={active ? 'secondary' : 'primary'}
                // invertedColor={!active}
            >
                {label ??
                    intl.formatMessage({
                        id: dest,
                        defaultMessage: toTitleCase(dest),
                    })}
            </Button>
        </Link>
    );
};

/**
 * Cool PIM Header [Language |  Products | Hierarchies]
 */
export const PimAppBar = () => {
    const intl = useIntl();
    const { defaultLanguage } = useLanguageCtx();
    const {
        enableSearch,
        theme: themeSettings,
        jsonataDependenciesEnabled,
        globalSearch: globalSearchSettings,
    } = useContext(SettingsContext);
    const { hasUserRole, sessionData } = useAuth();
    const hasLanguages = useMemo(() => !!defaultLanguage, [defaultLanguage]);
    const { canTranslate } = useTranslations();
    const { menuItems: companyMenuItems } = useCompanyOverrides();
    const customPages = useCustomPages();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const displayUserName =
        sessionData?.name || sessionData?.surname
            ? `${sessionData?.name} ${sessionData.surname}`
            : sessionData?.username;

    return (
        <AppBar position="fixed" color="transparent" divider>
            <Box display="flex">
                <img
                    style={{ height: '32px', marginRight: '40px' }}
                    alt="CoolPim"
                    src={sessionData?.attributes?.logo ?? themeSettings?.logo ?? '/logoBlack.png'}
                />
                <Box display="flex" gap={2} marginLeft={t => t.spacing(1)}>
                    {hasUserRole(UI_APPBAR_ROLES.LANGUAGE) && (
                        <LinkTo dest="language" icon={<LanguageIcon />} />
                    )}
                    {hasUserRole(UI_APPBAR_ROLES.PRODUCTS) && (
                        <LinkTo
                            dest="products"
                            icon={<BusinessCenterIcon />}
                            disabled={!hasLanguages}
                        />
                    )}
                    {hasUserRole(UI_APPBAR_ROLES.VIEWER_3D) && (
                        <LinkTo dest="3d-viewer" icon={<ViewInArIcon />} disabled={!hasLanguages} />
                    )}
                    {hasUserRole(UI_APPBAR_ROLES.HIERARCHIES) && (
                        <LinkTo
                            dest="hierarchies"
                            icon={<AccountTreeIcon />}
                            disabled={!hasLanguages}
                        />
                    )}
                    {hasUserRole(UI_APPBAR_ROLES.JOBS) && (
                        <LinkTo dest="jobs" icon={<WorkIcon />} disabled={!hasLanguages} />
                    )}
                    {canTranslate && hasUserRole(UI_APPBAR_ROLES.TRANSLATIONS) && (
                        <LinkTo dest={translationsPath} icon={<TranslateIcon />} />
                    )}
                    {enableSearch && hasUserRole(UI_APPBAR_ROLES.SEARCH) && (
                        <LinkTo dest="search" icon={<SearchIcon />} disabled={!hasLanguages} />
                    )}
                    {hasUserRole(UI_APPBAR_ROLES.DISCOUNTS) && (
                        <LinkTo
                            dest="discounts"
                            icon={<LocalOfferIcon />}
                            disabled={!hasLanguages}
                        />
                    )}
                    {hasUserRole(UI_APPBAR_ROLES.SHORT_LISTS) && (
                        <LinkTo dest="short_lists" icon={<ListIcon />} disabled={!hasLanguages} />
                    )}
                    {hasUserRole(UI_APPBAR_ROLES.DATA_QUALITY) && (
                        <LinkTo
                            dest="data-quality"
                            icon={<AssessmentIcon />}
                            disabled={!hasLanguages}
                        />
                    )}
                    {hasUserRole(UI_APPBAR_ROLES.OFFERS) && (
                        <LinkTo dest="offers" icon={<DescriptionIcon />} disabled={!hasLanguages} />
                    )}
                    {hasUserRole(UI_APPBAR_ROLES.SETTINGS) && (
                        <LinkTo dest="settings" icon={<SettingsIcon />} disabled={!hasLanguages} />
                    )}
                    {hasUserRole(UI_APPBAR_ROLES.PRINTOUT_BUILDER) && (
                        <LinkTo
                            dest="printout-builder"
                            icon={<SettingsIcon />}
                            disabled={!hasLanguages}
                        />
                    )}
                    {hasUserRole('PIM_SUPERADMIN') &&
                        hasUserRole(UI_APPBAR_ROLES.ADMIN_DASHBOARD) && (
                            <LinkTo
                                dest="admin-dashboard"
                                icon={<DashboardIcon />}
                                label={intl.formatMessage({
                                    id: 'nav.admin-dashboard',
                                    defaultMessage: 'Admin Dashboard',
                                })}
                            />
                        )}
                    {hasUserRole('COMPANY_ADMIN') && hasUserRole(UI_APPBAR_ROLES.COMPANY_ADMIN) && (
                        <LinkTo
                            dest="company-admin"
                            icon={<DashboardIcon />}
                            label={intl.formatMessage({
                                id: 'nav.company-admin',
                                defaultMessage: 'My Company',
                            })}
                        />
                    )}
                    {hasUserRole('PIM_CONFIGIT_ADMIN') && (
                        <LinkTo
                            dest="configit"
                            icon={<ExtensionIcon />}
                            label={intl.formatMessage({
                                id: 'nav.configit',
                                defaultMessage: 'Configit',
                            })}
                        />
                    )}
                    {hasUserRole('PIM_ADMIN') && jsonataDependenciesEnabled === true && (
                        <LinkTo
                            dest="jsonata-dependencies"
                            icon={<AccountTreeIcon />}
                            label={intl.formatMessage({
                                id: 'nav.jsonata-dependencies',
                                defaultMessage: 'JSONata Dependencies',
                            })}
                            disabled={!hasLanguages}
                        />
                    )}
                    {customPages.map(page => (
                        <LinkTo
                            key={page.id}
                            dest={`pages/${page.slug}`}
                            icon={resolveIcon(page.icon)}
                            label={page.name}
                            disabled={!hasLanguages}
                        />
                    ))}
                    {companyMenuItems.map(item => (
                        <LinkTo
                            key={item.id}
                            dest={item.path}
                            icon={item.icon}
                            disabled={!hasLanguages}
                        />
                    ))}
                </Box>
            </Box>
            {displayUserName && (
                <Box display="flex" marginLeft="auto" alignItems="center" ml="auto">
                    <Box>
                        {globalSearchSettings?.hierarchyIds?.length &&
                            hasUserRole(UI_APPBAR_ROLES.GLOBAL_SEARCH) && <GlobalSearchInput />}
                    </Box>
                    <Box ml={2}>{hasUserRole(UI_APPBAR_ROLES.CART) && <CartNavButton />}</Box>
                    <Box ml={2}>
                        {hasUserRole(UI_APPBAR_ROLES.NOTIFICATIONS) && <NotificationBell />}
                    </Box>
                    <Box
                        ml={2}
                        display="flex"
                        alignItems="center"
                        flexWrap="wrap"
                        // marginLeft="auto"
                    >
                        <Button
                            variant="text"
                            color="primary"
                            startIcon={<AccountCircleIcon />}
                            onClick={handleClick}
                        >
                            {displayUserName}
                        </Button>

                        <Menu
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            open={open}
                            anchorEl={anchorEl}
                            onClose={handleClose}
                        >
                            <MenuItem
                                onClick={() => {
                                    window.location.href = '/logout.html';
                                }}
                            >
                                {intl.formatMessage({ defaultMessage: 'Logout', id: 'logout' })}
                            </MenuItem>
                        </Menu>
                    </Box>
                </Box>
            )}
        </AppBar>
    );
};

export default PimAppBar;
